import { useTheme, colorSchemes, modes } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Check, Sun, Moon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ThemeSelector = () => {
  const { mode, colorScheme, updateMode, updateColorScheme, saveThemeToProfile } = useTheme();
  const { user } = useAuth();

  const handleSave = async () => {
    await saveThemeToProfile();
    toast.success('Theme preferences saved!');
  };

  return (
    <Card className="bg-card/50 border-border/50" data-testid="theme-selector">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div 
            className="w-5 h-5 rounded-full"
            style={{ background: `linear-gradient(135deg, var(--gradient-from), var(--gradient-to))` }}
          />
          Appearance
        </CardTitle>
        <CardDescription>
          Customize how RollM8s looks for you. Changes apply instantly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mode Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground">Mode</label>
          <div className="flex gap-3">
            <button
              onClick={() => updateMode('dark')}
              className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                mode === 'dark' 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border/50 hover:border-primary/50'
              }`}
              data-testid="mode-dark"
            >
              <Moon className="w-5 h-5" />
              <span className="font-medium">Dark</span>
              {mode === 'dark' && <Check className="w-4 h-4 text-primary ml-auto" />}
            </button>
            <button
              onClick={() => updateMode('light')}
              className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                mode === 'light' 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border/50 hover:border-primary/50'
              }`}
              data-testid="mode-light"
            >
              <Sun className="w-5 h-5" />
              <span className="font-medium">Light</span>
              {mode === 'light' && <Check className="w-4 h-4 text-primary ml-auto" />}
            </button>
          </div>
        </div>

        {/* Color Scheme Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground">Color Scheme</label>
          <div className="max-h-48 overflow-y-auto rounded-xl border-2 border-border p-2 grid grid-cols-4 sm:grid-cols-5 gap-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--primary) transparent' }}>
            {Object.entries(colorSchemes).filter(([key]) => !['jamaica','usa','uk','nigeria','brazil','trinidad','barbados','ghana','ireland','italy','mexico','japan','southafrica','colombia','india'].includes(key)).map(([key, scheme]) => (
              <button
                key={key}
                onClick={() => updateColorScheme(key)}
                className={`relative flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                  colorScheme === key 
                    ? 'bg-primary/15 border-primary ring-1 ring-primary' 
                    : 'border-transparent hover:bg-primary/5 hover:border-border'
                }`}
                data-testid={`color-${key}`}
                title={scheme.name}
              >
                <div 
                  className="w-8 h-8 rounded-full shadow-md"
                  style={{ 
                    background: `linear-gradient(135deg, ${scheme.gradientFrom}, ${scheme.gradientTo})` 
                  }}
                />
                <span className="text-[10px] font-medium truncate w-full text-center">{scheme.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Flag Themes */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground">Flag Themes</label>
          <div className="max-h-48 overflow-y-auto rounded-xl border-2 border-border p-2 grid grid-cols-4 sm:grid-cols-5 gap-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--primary) transparent' }}>
            {Object.entries(colorSchemes).filter(([key]) => ['jamaica','usa','uk','nigeria','brazil','trinidad','barbados','ghana','ireland','italy','mexico','japan','southafrica','colombia','india'].includes(key)).map(([key, scheme]) => (
              <button
                key={key}
                onClick={() => updateColorScheme(key)}
                className={`relative flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                  colorScheme === key 
                    ? 'bg-primary/15 border-primary ring-1 ring-primary' 
                    : 'border-transparent hover:bg-primary/5 hover:border-border'
                }`}
                data-testid={`color-${key}`}
                title={scheme.name}
              >
                <div 
                  className="w-8 h-8 rounded-full shadow-md"
                  style={{ 
                    background: `linear-gradient(135deg, ${scheme.gradientFrom}, ${scheme.gradientTo})` 
                  }}
                />
                <span className="text-[10px] font-medium truncate w-full text-center">{scheme.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Preview — shows what the theme colour actually affects */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground">Live Preview</label>
          <div className="p-4 rounded-xl bg-card border border-border/50 space-y-3">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex-shrink-0"
                style={{ background: `linear-gradient(135deg, var(--gradient-from), var(--gradient-to))` }}
              />
              <div>
                <p className="font-semibold text-base">Your Colour</p>
                <p className="text-xs text-muted-foreground">This tints buttons, links, badges and accents across the entire app</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-[10px] text-muted-foreground">
              <div>
                <Button size="sm" className="w-full pointer-events-none">Book Ride</Button>
                <p className="mt-1">Buttons</p>
              </div>
              <div>
                <div className="h-9 rounded-md border-2 border-primary flex items-center justify-center text-primary text-xs font-medium">Active</div>
                <p className="mt-1">Filters</p>
              </div>
              <div>
                <div className="h-9 rounded-md flex items-center justify-center">
                  <span className="text-primary text-sm font-semibold">Link</span>
                </div>
                <p className="mt-1">Links</p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button — only meaningful for signed-in users (theme
            persists via localStorage for everyone, but signed-in users
            also sync to their profile so it follows them across
            devices). */}
        {user ? (
          <Button onClick={handleSave} className="w-full" data-testid="save-theme-btn">
            Save Theme Preferences
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground text-center">
            Your theme is saved on this device. Sign up to sync it across devices.
          </p>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Theme changes are applied instantly.{user ? ' Click save to remember your preference.' : ''}
        </p>
      </CardContent>
    </Card>
  );
};

export default ThemeSelector;
